import deletedTabEdits from "@ext/git/core/Diff/logic/deletedTabEdits";
import { type BlockChange, type BlockDiffOptions, Diff, type DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { JSONContent } from "@tiptap/core";
import type { Node, Schema } from "prosemirror-model";

export type CompareOptions = BlockDiffOptions;

// Needs the full editor schema: `getSchema()` without args lacks plugin nodes and `nodeFromJSON` throws on them.
const compareDocuments = (
	before: JSONContent | Node,
	after: JSONContent | Node,
	schema: Schema,
	options: CompareOptions,
): DiffChange[] => {
	const toJson = (doc: JSONContent | Node): JSONContent =>
		typeof (doc as Node).toJSON === "function" ? (doc as Node).toJSON() : (doc as JSONContent);

	const beforeJson = toJson(before);
	const afterJson = toJson(after);

	const afterDoc = schema.nodeFromJSON(afterJson);
	const changes = new Diff(beforeJson, afterJson, schema, schema).diff(options);

	// Must match the shape of `editor.storage.diff.changes`: consumers rely on one format.
	const asDiffChanges: DiffChange[] = changes.map((change: BlockChange) => {
		if (change.type === "deleted") {
			const point = change.insertBeforePos ?? afterDoc.content.size;
			return { type: change.type, from: point, to: point, nodeA: change.nodeA, posA: change.posA };
		}

		const from = change.posB ?? 0;
		return {
			type: change.type,
			from,
			to: from + (change.nodeB?.nodeSize ?? 0),
			nodeA: change.nodeA,
			posA: change.posA,
			nodeB: change.nodeB,
			posB: change.posB,
		};
	});

	return deletedTabEdits(asDiffChanges, schema.nodeFromJSON(beforeJson), afterDoc);
};

export default compareDocuments;
